import React, { useMemo, useState } from "react";
import {
  ROLE_META,
  ScoreChip,
  fmtLevel,
  fmtRate,
  fmtMoney,
  fmtSignedMoney,
  isNum,
} from "./paeiPrimitives";

// The main screen. Two toggles, a summary strip, then one two-row block per RACI
// slot: the data row, and a full-width reason row underneath it.
//
// The reason gets its own row because it does not survive being squeezed into a
// column — and it is the point of the table.

function StageToggle({ value, onChange, sameCount, redesignCount, redesignAvailable }) {
  return (
    <div className="pa-seg" role="group" aria-label="Staffing stage">
      <button type="button" aria-pressed={value === "same"} onClick={() => onChange("same")}>
        Same structure <em>{sameCount}</em>
      </button>
      <button
        type="button"
        aria-pressed={value === "redesigned"}
        disabled={!redesignAvailable}
        onClick={() => onChange("redesigned")}
        title={redesignAvailable ? "" : "The redesign did not run for this process"}
      >
        After redesign <em>{redesignCount}</em>
      </button>
    </div>
  );
}

function ReassignmentRow({ row }) {
  const role = row.primary_role;
  const roleLabel = ROLE_META[role]?.label || role;
  const need = row.required ? row.required[role] : null;
  const changed = !!row.changed;

  return (
    <>
      <tr
        className={`pa-row ${changed ? "changed" : "kept"}${row.rationale ? "" : " no-reason"}`}
      >
        <td className="pa-cell-step">
          <div className="pa-step-name">{row.task_name}</div>
          <div className="pa-step-meta">TASK-{row.task_id}</div>
        </td>

        <td className="pa-cell-slot">
          <span className={`pa-slot-badge slot-${String(row.slot).toLowerCase()}`}>{row.slot}</span>
          <span className={`pa-tag ${changed ? "changed" : "kept"}`}>
            {changed ? "reassign" : "keep in place"}
          </span>
        </td>

        <td className="pa-cell-need">
          <span className={`pa-need role-${role}`}>
            {roleLabel} {fmtLevel(need)}
          </span>
        </td>

        <td className="pa-cell-person">
          <div className={`pa-person-name${changed ? " struck" : ""}`}>{row.from_job || "—"}</div>
          <div className="pa-person-meta">
            {fmtRate(row.from_rate)}
            {row.from_paei ? (
              <>
                {" · "}
                <ScoreChip score={row.from_paei[role]} required={need} role={role} />
              </>
            ) : null}
          </div>
        </td>

        <td className="pa-cell-arrow" aria-hidden="true">
          {changed ? "→" : "="}
        </td>

        <td className="pa-cell-person">
          <div className="pa-person-name">{row.to_job || "—"}</div>
          <div className="pa-person-meta">
            {fmtRate(row.to_rate)}
            {row.to_paei ? (
              <>
                {" · "}
                <ScoreChip score={row.to_paei[role]} required={need} role={role} />
              </>
            ) : null}
          </div>
        </td>

        <td className="pa-cell-cost">
          <div
            className={`pa-cost${
              isNum(row.saving) && Number(row.saving) !== 0 ?
                Number(row.saving) > 0 ? " saving" : " increase"
              : ""
            }`}
          >
            {fmtSignedMoney(row.saving)}
          </div>
          <div className="pa-cost-sub">
            {fmtMoney(row.from_cost)} → {fmtMoney(row.to_cost)}
          </div>
        </td>
      </tr>

      {row.rationale ? (
        <tr className="pa-reason-row">
          <td colSpan={7}>
            <span className="pa-reason-tag">Why</span>
            <span className="pa-reason-text">{row.rationale}</span>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function PaeiReassignmentsTab({ result }) {
  const sameRows = useMemo(() => result?.restaffing || [], [result]);
  const redesignRows = useMemo(() => result?.redesign?.staffing || [], [result]);
  const redesignAvailable = result?.redesign?.available !== false && redesignRows.length > 0;

  const [stage, setStage] = useState("same");
  const [changedOnly, setChangedOnly] = useState(true);

  // Genuinely different plans against different structures — different row counts
  // are expected, and neither may ever be labelled as the other.
  const rows = stage === "redesigned" && redesignAvailable ? redesignRows : sameRows;

  const visible = useMemo(
    () => (changedOnly ? rows.filter((r) => r.changed) : rows),
    [rows, changedOnly],
  );

  const changedCount = useMemo(() => rows.filter((r) => r.changed).length, [rows]);
  const netSaving = useMemo(
    () => rows.reduce((acc, r) => acc + (isNum(r.saving) ? Number(r.saving) : 0), 0),
    [rows],
  );

  return (
    <div className="pa-panel">
      <div className="pa-toolbar">
        <StageToggle
          value={stage}
          onChange={setStage}
          sameCount={sameRows.length}
          redesignCount={redesignRows.length}
          redesignAvailable={redesignAvailable}
        />
        <div className="pa-seg" role="group" aria-label="Row filter">
          <button type="button" aria-pressed={changedOnly} onClick={() => setChangedOnly(true)}>
            Changed only
          </button>
          <button type="button" aria-pressed={!changedOnly} onClick={() => setChangedOnly(false)}>
            All slots
          </button>
        </div>
      </div>

      <p className="pa-card-hint">
        {stage === "redesigned" ?
          "Staffing against the requirement as the heuristics left it. This is a different plan against a different structure — not the same table restaffed."
        : "Staffing the process exactly as it is declared today, slot by slot."}
      </p>

      <div className="pa-summary-strip">
        <span>
          <b>{changedCount}</b> of <b>{rows.length}</b> slots reassigned
        </span>
        <span className={netSaving > 0 ? "saving" : netSaving < 0 ? "increase" : ""}>
          Net cost change per case: <b>{fmtSignedMoney(netSaving)}</b>
        </span>
      </div>

      {visible.length === 0 ? (
        <div className="pa-empty">
          {rows.length === 0 ?
            "No staffing rows in this plan."
          : "No slot changed under this plan. Switch to “All slots” to see every slot as it stands."}
        </div>
      ) : (
        <div className="pa-tbl-scroll">
          <table className="pa-tbl pa-reassign-tbl">
            <thead>
              <tr>
                <th>Step</th>
                <th>Slot</th>
                <th>What the slot needs</th>
                <th>Previously assigned</th>
                <th aria-label="direction" />
                <th>Now assigned</th>
                <th className="pa-num">Cost / case</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <ReassignmentRow key={`${r.task_id}-${r.slot}-${i}`} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="pa-note">
        The score chip shows the holder's level on <b>{"the slot's own primary role"}</b> — the one
        dimension that slot is judged on. Do not read <b>R</b> as Producer: the Responsible is held
        to whatever the step needs most.
      </p>
    </div>
  );
}

export default PaeiReassignmentsTab;
